import { useTranslations } from "use-intl";

import { FallbackImage } from "@/components/fallback-image";
import {
  buildReleaseGalleryImageSrcSet,
  buildReleaseGalleryImageUrl,
  buildReleaseImageSrcSet,
  buildReleaseImageUrl,
} from "@garimpa/lib/imageUrls";

const FANNED_LAYOUTS = {
  2: { positions: [-18, 18], scale: 0.64 },
  3: { positions: [-22, 0, 22], scale: 0.56 },
  4: { positions: [-27, -9, 9, 27], scale: 0.46 },
  5: { positions: [-30, -15, 0, 15, 30], scale: 0.4 },
} as const;

function imageTransform(imageIndex: number, renderedImageCount: number, fanned: boolean): string {
  if (fanned) {
    const layout = FANNED_LAYOUTS[renderedImageCount as keyof typeof FANNED_LAYOUTS];
    const position = layout?.positions[imageIndex] ?? 0;
    const centerIndex = (renderedImageCount - 1) / 2;
    const rotation = (imageIndex - centerIndex) * 2.5;
    return `translateX(${position}%) rotate(${rotation}deg) scale(${layout?.scale ?? 1})`;
  }
  if (imageIndex === 0) return "translate(-4px, 4px)";
  const offset = imageIndex * 7;
  return `translate(${offset}px, ${-offset}px) rotate(${imageIndex * 1.5}deg)`;
}

export function ReleaseCardImageLayer({
  fanned,
  hasKnownImage,
  imageIndex,
  releaseId,
  renderedImageCount,
  title,
  totalImageCount,
}: {
  fanned: boolean;
  hasKnownImage: boolean;
  imageIndex: number;
  releaseId: string;
  renderedImageCount: number;
  title: string;
  totalImageCount: number;
}) {
  const t = useTranslations("release");
  const imageLabel = t("photoOf", { number: imageIndex + 1, total: totalImageCount });
  const image =
    imageIndex === 0 ? (
      <FallbackImage
        src={hasKnownImage ? buildReleaseImageUrl(releaseId, { width: 480 }) : null}
        srcSet={hasKnownImage ? buildReleaseImageSrcSet(releaseId, [320, 480, 640]) : undefined}
        alt={title}
        showLoadingSpinner
        objectFit="contain"
        sizes="(min-width: 1280px) 28vw, (min-width: 640px) 44vw, 88vw"
      />
    ) : (
      <FallbackImage
        src={buildReleaseGalleryImageUrl(releaseId, imageIndex, { width: 480 })}
        srcSet={buildReleaseGalleryImageSrcSet(releaseId, imageIndex, [320, 480, 640])}
        alt={`${title} - ${imageLabel}`}
        objectFit="contain"
        sizes="(min-width: 1280px) 28vw, (min-width: 640px) 44vw, 88vw"
      />
    );
  const layerClassName =
    "absolute inset-0 overflow-hidden rounded-md border border-border/80 bg-background shadow-lg transition-[transform,box-shadow] duration-300 ease-out motion-reduce:transition-none";
  const layerStyle = {
    transform: imageTransform(imageIndex, renderedImageCount, fanned),
    zIndex: renderedImageCount - imageIndex,
  };

  return (
    <div className={layerClassName} style={layerStyle}>
      {image}
    </div>
  );
}
